import React, { useEffect, useState } from "react";
import { Card, Spinner, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";

import CustomPagination from "Common/customPagination";
import { authServices } from "Utils/auth/services";
import PlUserTable from "Common/Tables/eventWiseTable/plUserTable";

interface EventPlPayload {
  page: number;
  limit: number;
  market_id: string;
}

const PlUserWise: React.FC = () => {
  const { event_id } = useParams<{ event_id: string }>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [casinoBetsData, setCasinoBetsData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(50);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const getEventPlData = async (page: number = 1, limit: number = 50) => {
    if (!event_id) return;

    setIsLoading(true);
    try {
      const payload: EventPlPayload = {
        page,
        limit,
        market_id: event_id,
      };

      const { response } = await authServices.usersPLByMarket(payload);
      if (response?.data) {
        setCasinoBetsData(response.data);
        setTotalItems(response.total || 0);
      } else {
        setCasinoBetsData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching casino bets data:", error);
      setCasinoBetsData([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getEventPlData(page, itemsPerPage);
  };

  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    getEventPlData(1, newItemsPerPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (
    match_id: string,
    match_name: string,
    sport_name: string,
    id: string,
    type: string
  ) => {
    const url = `/plUserBet/${match_id}/${event_id}/${match_name}/${sport_name}/${id}/${type}`;
    window.open(url, "_blank");
  };

  const filteredData = casinoBetsData.filter((item) => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    
    // Ensure we are only calling toLowerCase on valid strings
    const matchName = item.match_name?.toLowerCase() || "";
    const sportName = item.sport_name?.toLowerCase() || "";
    const winnerName = item.winner_name?.toLowerCase() || "";
    const betType = item.bet_type?.toLowerCase() || "";
  
    return (
      matchName.includes(lowercasedSearchTerm) ||
      sportName.includes(lowercasedSearchTerm) ||
      winnerName.includes(lowercasedSearchTerm) ||
      betType.includes(lowercasedSearchTerm)
    );
  });

  useEffect(() => {
    getEventPlData(currentPage, itemsPerPage);
  }, [event_id, itemsPerPage]);

  return (
    <div className="event-pl">
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex gap-2 align-items-center">
              Show
              <select
                className="form-select w-auto"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={150}>150</option>
                <option value={200}>200</option>
              </select>
              Entries
            </div>

            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-25"
            />
          </div>

          {isLoading ? (
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <>
              <PlUserTable
                fancyPLData={filteredData}
                onClickRow={handleRowClick}
              />
              <CustomPagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PlUserWise;
